import { motion } from 'framer-motion'
import { Server, Database, Code2, Headphones } from 'lucide-react'
import { skills } from '../data/portfolioData'
import { SKILLS } from '../constants/testIds'

const iconMap = { Server, Database, Code2, Headphones }

export default function Skills() {
  return (
    <section id="skills" data-testid={SKILLS.section} className="py-16 md:py-24 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-5">
          <span className="font-mono text-[#0047AB] text-xs tracking-[0.3em] uppercase">Capabilities</span>
          <span className="h-px flex-1 max-w-[60px] bg-[#0047AB]/25" />
        </motion.div>

        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.05 }}
          className="font-sans font-bold text-4xl md:text-5xl tracking-tight text-[#111111] mb-10 md:mb-14">
          Full stack.
          <br /><span className="text-[#6B7280]">Infrastructure to interface.</span>
        </motion.h2>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-14">
          {skills.map((skill, i) => <SkillCard key={skill.category} skill={skill} index={i} />)}
        </div>

        {/* Tool strip */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
          viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="border-t border-[#E5E7EB] pt-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA3AF] mb-5">Technologies &amp; Tools</div>
          <div className="flex flex-wrap gap-2">
            {['React', 'FastAPI', 'MongoDB', 'Python', 'TCP/IP', 'CCTV Installation', 'POS Systems',
              'Email Hosting', 'Security Systems', 'PA Systems', 'Data Cabling', 'Dell EMC',
              'Linux', 'Windows Server', 'CRM', 'HRMS'].map((tool) => (
              <motion.span key={tool} whileHover={{ borderColor: 'rgba(0,71,171,0.35)', color: '#374151' }}
                className="px-3 py-1.5 text-xs font-mono text-[#9CA3AF] border border-[#E5E7EB] rounded-lg bg-white cursor-default"
                style={{ transition: 'border-color 0.2s, color 0.2s' }}>
                {tool}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function SkillCard({ skill, index }) {
  const Icon = iconMap[skill.icon] || Server
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: index * 0.07 }}
      whileHover={{ y: -2 }}
      className="group p-7 border border-[#E5E7EB] rounded-xl bg-white shadow-card hover:shadow-card-hover hover:border-[#0047AB]/20 transition-all duration-300"
      data-testid={`skill-card-${index}`}
      style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease' }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="p-2.5 bg-[#EBF2FF] border border-[#0047AB]/15 rounded-xl group-hover:bg-[#0047AB] transition-colors duration-300">
          <Icon size={15} className="text-[#0047AB] group-hover:text-white transition-colors duration-300" />
        </div>
        <span className="font-mono text-[#D1D5DB] text-xs">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <h3 className="font-sans font-semibold text-[#111111] text-base md:text-lg mb-4">{skill.category}</h3>
      <div className="flex flex-wrap gap-2">
        {skill.items.map((item) => (
          <span key={item}
            className="px-2.5 py-1 text-xs font-body text-[#6B7280] border border-[#E5E7EB] rounded-lg bg-[#F3F4F6] group-hover:text-[#374151] transition-colors duration-200">
            {item}
          </span>
        ))}
      </div>
    </motion.div>
  )
}
